import { CmsPostCategoryId, getCmsPostsByCategory } from "@/features/cms";
import { PostSchema } from "@/features/cms/types";
import { CmsPost } from "@/store/services/internal";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { getNestedPostsByCategory, getPostByIdAndCategory } from "../utils";

const useCmsPosts = (
  category: CmsPostCategoryId | "archetypes",
  selectedPostId?: string,
  selectedParentPostId?: string,
  sort?: "alphabetical" | "verbatim"
) => {
  const router = useRouter();
  const pathname = usePathname();
  const [status, setStatus] = useState<"loading" | "done">("loading");
  const [posts, setPosts] = useState<CmsPost[]>([]);
  const [parentPost, setParentPost] = useState<CmsPost>();

  const getSelectablePosts = (posts: CmsPost[], postSchemas: PostSchema[]) => {
    // filter out group menu items that cannot be used a default selection
    const flatPostSchemas = new Set(
      postSchemas
        .filter(
          (postSchema) =>
            postSchema?.sectionType !== "nested" &&
            postSchema?.sectionType !== "grouped" &&
            postSchema?.sectionType !== "direct"
        )
        .map((schema) => schema.id)
    );
    const filtered = posts.filter((post) => flatPostSchemas.has(post.id));
    return sort === "verbatim" ? filtered : filtered.sort((a, b) => a.title.localeCompare(b.title));
  };

  useEffect(() => {
    if (category === "archetypes") {
      setStatus("done");
      return;
    }

    setStatus("loading");
    if (selectedParentPostId) {
      getPostByIdAndCategory(selectedParentPostId, category).then((post) => {
        setParentPost(post);
      });
      getNestedPostsByCategory(category, selectedParentPostId)
        .then((data: { posts: CmsPost[]; postSchemas: PostSchema[] } | undefined) => {
          if (!data) {
            return;
          }
          const { posts, postSchemas } = data;
          const allowedPosts = getSelectablePosts(posts, postSchemas);
          if (selectedPostId === "_" && allowedPosts && pathname) {
            router.replace(pathname.replace("_", allowedPosts[0].slug ?? "404"));
          }

          setPosts(posts || []);
          setStatus("done");
        })
        .catch(() => setStatus("done"));
    } else {
      getCmsPostsByCategory(category)
        .then((data?: { posts: CmsPost[]; postSchemas: PostSchema[] }) => {
          if (!data) {
            setStatus("done");
            return;
          }
          const { posts, postSchemas } = data;

          const allowedPosts = getSelectablePosts(posts, postSchemas);
          if (selectedPostId === "_" && allowedPosts) {
            router.replace(`/${category}/${allowedPosts[0]?.slug ?? "404"}`);
          }
          setPosts(posts || []);
          setStatus("done");
        })
        .catch(() => setStatus("done"));
    }
  }, [category, selectedPostId, router]);

  return { status, posts, parentPost };
};
export default useCmsPosts;
